// Shared constants + tiny helpers used across the League-workspace views.
// The old monolithic League.jsx defined all of these inline; splitting
// them out here keeps each per-tab view small and focused.

import {
  Landmark, ShieldCheck, CalendarDays, Gavel, Megaphone,
  Crown, Users as UsersIcon, Sparkles, ScrollText
} from "lucide-react";

// Sidebar / bottom-tab menu items for the League workspace.
export const LEAGUE_NAV = [
  { key: "committee",     label: "Executive Committee", icon: Landmark },
  { key: "teams",         label: "Teams Management",    icon: ShieldCheck },
  { key: "schedule",      label: "Schedule Management", icon: CalendarDays },
  { key: "umpires",       label: "Umpiring Pool",       icon: Gavel },
  { key: "announcements", label: "Announcements",       icon: Megaphone }
];

export const ROLE_META = {
  PRESIDENT:             { label: "President",                Icon: Crown,      tone: "gold"   },
  VICE_PRESIDENT:        { label: "Vice President",           Icon: Sparkles,   tone: "orange" },
  GENERAL_SECRETARY:     { label: "General Secretary",        Icon: Landmark,   tone: "cyan"   },
  TREASURER:             { label: "Finance Head",             Icon: Landmark,   tone: "green"  },
  RULES_HEAD:            { label: "Rules Head",               Icon: ScrollText, tone: "red"    },
  UMPIRING_HEAD:         { label: "Umpiring Head",            Icon: Gavel,      tone: "cyan"   },
  SOCIAL_MEDIA_ADMIN:    { label: "Social Media & Web Admin", Icon: Megaphone,  tone: "orange" },
  EXEC_COMMITTEE_MEMBER: { label: "Committee Members",        Icon: UsersIcon,  tone: "violet" },
  ADMIN:                 { label: "Administrators",           Icon: Landmark,   tone: "red"    }
};

// Role-level bios + key contributions surfaced on the Executive
// Committee landing page. These describe the position itself so the
// text stays evergreen as holders change season over season.
export const ROLE_BIOS = {
  PRESIDENT: {
    title: "President",
    bio: "Serves as the President of PCL, providing overall leadership and direction for the league. Sets the strategic vision, governance principles, and long-term growth trajectory of Pittsburgh cricket, and represents the league in every external engagement. Focused on governance, transparency, and building a sustainable, competitive cricket ecosystem in Pittsburgh.",
    contributions: [
      "Driving league vision, governance, and long-term growth",
      "Strengthening organizational structure and transparency",
      "Building a scalable, competitive league ecosystem",
      "Leading by example both on and off the field"
    ]
  },
  VICE_PRESIDENT: {
    title: "Vice President",
    bio: "Serves as the Vice President of PCL, partnering closely with the President on the league's strategic initiatives. Provides operational stability and continuity, ensuring league objectives translate into action season over season, and steps in for the President whenever required.",
    contributions: [
      "Supporting strategic planning and execution",
      "Strengthening governance and operational consistency",
      "Driving league initiatives and team coordination",
      "Deputising for the President across engagements"
    ]
  },
  GENERAL_SECRETARY: {
    title: "General Secretary",
    bio: "Serves as the General Secretary of PCL, acting as the central operational leader responsible for day-to-day league management. Ensures smooth coordination across teams, upholds operational discipline, maintains league records, and keeps communication channels crisp between captains and the committee.",
    contributions: [
      "Managing league operations and communications",
      "Ensuring coordination between teams and leadership",
      "Driving operational efficiency and execution",
      "Maintaining league records and documentation"
    ]
  },
  TREASURER: {
    title: "Finance Head",
    bio: "Serves as the Finance Head of PCL, overseeing the league's financial planning, budgeting, and governance. Ensures the league operates with financial discipline, transparency, and sustainability — protecting fiscal health season over season while enabling investment in the sport.",
    contributions: [
      "Managing financial planning and budgeting",
      "Ensuring transparency and accountability in finances",
      "Overseeing team dues and expense management",
      "Supporting sustainable league growth"
    ]
  },
  RULES_HEAD: {
    title: "Rules Head",
    bio: "Serves as the Rules Head of PCL, ensuring that the league operates within a clear, consistent, and well-defined regulatory framework. Plays a vital role in defining, interpreting, and enforcing league rules — ensuring fairness and uniformity across every competition.",
    contributions: [
      "Defining and maintaining league rules and policies",
      "Interpreting complex match scenarios",
      "Supporting dispute resolution with fairness",
      "Educating captains and umpires on rule updates"
    ]
  },
  UMPIRING_HEAD: {
    title: "Umpiring Head",
    bio: "Serves as the Umpiring Head of PCL, responsible for upholding the integrity, fairness, and spirit of the game. Ensures consistency in officiating, mentors umpires, and maintains high standards of decision-making across all matches.",
    contributions: [
      "Standardising umpiring quality across the league",
      "Mentoring and developing umpires",
      "Ensuring adherence to cricket laws and league rules",
      "Coordinating weekend match assignments"
    ]
  },
  SOCIAL_MEDIA_ADMIN: {
    title: "Social Media & Web Admin",
    bio: "Serves as the Social Media & Web Admin of PCL, driving the league's digital presence and communication strategy. Ensures the league remains connected, visible, and professionally represented — managing everything from match updates to player data and online engagement.",
    contributions: [
      "Managing website and digital platforms",
      "Publishing match updates and league communications",
      "Enhancing community engagement and visibility",
      "Coordinating with captains on team announcements"
    ]
  },
  EXEC_COMMITTEE_MEMBER: {
    title: "Executive Committee Member",
    bio: "Serves as an Executive Committee Member of PCL, contributing to strategic decisions, policy formation, and league governance. Represents the interests of teams and players, volunteers time to keep the league operating fairly, and champions the values that make PCL a community-driven organisation.",
    contributions: [
      "Contributing to strategic decisions and policy",
      "Representing team and player interests",
      "Supporting committee initiatives and events",
      "Upholding league values and standards"
    ]
  }
};

export const TEAM_TYPE_META = {
  Existing:        { label: "Returning Team",        hint: "Same identity as last season.",              tone: "cyan"   },
  ExistingRenamed: { label: "Returning · New Name",  hint: "Returning team competing under a new name.", tone: "violet" },
  New:             { label: "Brand New Team",        hint: "First season in the league.",                tone: "green"  }
};

export const STATUS_META = {
  Pending:  { label: "Pending review", tone: "gold"  },
  Approved: { label: "Approved",       tone: "green" },
  Rejected: { label: "Rejected",       tone: "red"   }
};

export const GROUND_SUGGESTIONS = ["Linbrook", "CC-1", "CC-2"];
export const UMPIRE_LEVELS      = ["Elite", "Panel", "Local", "Trainee"];

export const EMPTY_TEAM = {
  teamType: "New",
  teamName: "",
  existingTeamId: "",
  captainFirstName: "",
  captainLastName: "",
  captainEmail: "",
  notes: ""
};

export const EMPTY_MATCH = {
  series: "", date: "", time: "", teamOne: "", teamTwo: "", ground: "", notes: ""
};

export const EMPTY_UMPIRE = {
  firstName: "", lastName: "", teamName: "", email: "", phone: "", isActive: true
};

export function fmtMatchDate(iso) {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00");
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" });
}

export function fmt12(time) {
  if (!time) return "";
  const m = /^(\d{1,2}):(\d{2})$/.exec(time);
  if (!m) return time;
  let h = Number(m[1]);
  const suffix = h >= 12 ? "PM" : "AM";
  h = h % 12; if (h === 0) h = 12;
  return `${h}:${m[2]} ${suffix}`;
}
